import {
  CsvCellValue,
  CsvExportContext,
  CsvExportDefinition,
  ResolvedCsvRows
} from "./types";
import { csvHeaders, visibleColumns } from "./serializeCsv";

export interface ResolveCsvRowsOptions {
  concurrency?: number;
}

/**
 * Resolves cell getters with bounded concurrent work while preserving the
 * definition's visual row and column ordering.
 */
export const resolveCsvRows = async <T extends { id?: CsvCellValue }>(
  definition: CsvExportDefinition<T>,
  records: T[],
  context: CsvExportContext = {},
  options: ResolveCsvRowsOptions = {}
): Promise<ResolvedCsvRows> => {
  const columns = visibleColumns(definition);
  const tasks = records.flatMap((record, recordIndex) =>
    columns.map((column, columnIndex) => ({
      record,
      recordIndex,
      column,
      columnIndex
    }))
  );
  const values: CsvCellValue[][] = records.map((record) => [record.id]);
  const concurrency = Math.max(1, Math.floor(options.concurrency || 8));
  let nextTask = 0;

  const worker = async () => {
    while (nextTask < tasks.length) {
      const task = tasks[nextTask++];
      const value = await task.column.getValue(task.record, context);
      values[task.recordIndex][task.columnIndex + 1] = value;
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(concurrency, tasks.length) }, () => worker())
  );

  return { headers: csvHeaders(definition), rows: values };
};
